import { useMemo } from "react";

import { ProtectedLink } from "@impero-com/common/components/protectedLink/index";
import { TFunction, useTranslation } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import {
    Tag,
    TST_BLESSED_ID_COLUMN_SELECTION,
    TST_COLUMN_SIZE,
    TST_DEFAULT_PAGE_SIZE,
} from "@impero-com/ui";
import {
    usePresetTableSearchParams,
    ZodCodecMapToType,
} from "@impero-com/ui/domain";
import * as Sentry from "@sentry/react";
import {
    Column,
    createColumnHelper,
    getCoreRowModel,
    getFacetedMinMaxValues,
    getFacetedUniqueValues,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table";
import { Checkbox } from "antd";

import { EntityForUserListDto, StandardAccessRight } from "api/generated/model";
import { TSTValidMultipleOptionFilterSelect } from "components/table/tsTable/TSTValidMultipleOptionFilterSelect";
import { WebRoute } from "routes";
import { formatAccessRightRole } from "utils/accessRights";
import { createFilterFnWithMode } from "utils/table/createFilterFnWithMode";
import { createFilterHelpersGetter } from "utils/table/getTypedFilterValue";
import { tstArrIncludesSomeFilterFn } from "utils/table/tstArrIncludesSomeFilterFn";

import {
    AccessRightColumnId,
    CONTROL_ENTITIES_LIST_COLUMN_IDS,
    CONTROL_ENTITIES_TABLE_STATE_STORAGE_KEY,
    controlEntitiesListColumnFiltersCodecMap,
    controlEntitiesListTableDefaultValues,
} from "./consts";

export interface ControlEntityRow {
    id: number;
    name: string;
    description: string | null;
    controlAccessRight: StandardAccessRight | null;
    riskAccessRight: StandardAccessRight | null;
    hasAccess: boolean;
}

export function useUserControlEntitiesListTable(data: EntityForUserListDto) {
    const { t } = useTranslation();

    const tableData = useMemo<ControlEntityRow[]>(
        () =>
            data.content.map(
                ({
                    resource: { entity, controlAccessRight, riskAccessRight },
                    actions,
                }) => ({
                    id: entity.id,
                    name: entity.name,
                    description: entity.description ?? null,
                    controlAccessRight: controlAccessRight ?? null,
                    riskAccessRight: riskAccessRight ?? null,
                    hasAccess: actions.view,
                })
            ),
        [data]
    );
    const notificationLabel = t("Table_RemovedFiltersWarning");

    const stateAndOnChanges = usePresetTableSearchParams(
        CONTROL_ENTITIES_TABLE_STATE_STORAGE_KEY,
        controlEntitiesListTableDefaultValues,
        controlEntitiesListColumnFiltersCodecMap,
        notificationLabel,
        Sentry.captureException
    );
    const columns = buildColumns(t);
    const table = useReactTable({
        ...stateAndOnChanges,
        data: tableData,
        columns,
        getRowId: (row) => row.id.toString(),
        initialState: {
            ...stateAndOnChanges.initialState,
            pagination: {
                pageSize: TST_DEFAULT_PAGE_SIZE,
                pageIndex: 0,
            },
        },
        enableSortingRemoval: false,
        getCoreRowModel: getCoreRowModel<ControlEntityRow>(),
        getSortedRowModel: getSortedRowModel<ControlEntityRow>(),
        getFilteredRowModel: getFilteredRowModel<ControlEntityRow>(),
        getFacetedMinMaxValues: getFacetedMinMaxValues<ControlEntityRow>(),
        getFacetedUniqueValues: getFacetedUniqueValues<ControlEntityRow>(),
        getPaginationRowModel: getPaginationRowModel(),
    });

    const filterComponent = (column: Column<ControlEntityRow>) =>
        getFilterComponent(t, column);

    return {
        table,
        getFilterComponent: filterComponent,
        tableId: CONTROL_ENTITIES_TABLE_STATE_STORAGE_KEY,
    };
}

const columnHelper = createColumnHelper<ControlEntityRow>();

function accessRightColumn(id: AccessRightColumnId, header: string) {
    return columnHelper.accessor((row) => row[id] ?? undefined, {
        id,
        cell: (info) => {
            const accessRight = info.getValue();
            return (
                accessRight && (
                    <Tag variant="accessRights">
                        {formatAccessRightRole(accessRight)}
                    </Tag>
                )
            );
        },
        enableSorting: true,
        sortingFn: "alphanumeric",
        sortUndefined: "last",
        enableGlobalFilter: false,
        enableColumnFilter: true,
        filterFn: createFilterFnWithMode<ControlEntityRow, string[]>(
            (row, value) => tstArrIncludesSomeFilterFn(row, id, value)
        ),
        header: () => header,
        size: TST_COLUMN_SIZE.L,
    });
}

function buildColumns(t: TFunction) {
    return [
        columnHelper.display({
            id: TST_BLESSED_ID_COLUMN_SELECTION,
            header: (info) => (
                <Checkbox
                    checked={info.table.getIsAllPageRowsSelected()}
                    indeterminate={info.table.getIsSomePageRowsSelected()}
                    onChange={info.table.getToggleAllRowsSelectedHandler()}
                />
            ),
            size: TST_COLUMN_SIZE.CHECKBOX,
            meta: { fixedWidth: true },
            cell: (info) => (
                <Checkbox
                    checked={info.row.getIsSelected()}
                    onClick={() => info.row.toggleSelected()}
                />
            ),
        }),
        columnHelper.accessor((row) => row.name, {
            id: CONTROL_ENTITIES_LIST_COLUMN_IDS.NAME,
            cell: (info) => (
                <ProtectedLink
                    to={`${WebRoute.ENTITIES}/${info.row.original.id}`}
                    hasAccess={info.row.original.hasAccess}
                >
                    {info.getValue()}
                </ProtectedLink>
            ),
            enableSorting: true,
            sortingFn: "alphanumeric",
            enableColumnFilter: true,
            filterFn: createFilterFnWithMode<ControlEntityRow, string[]>(
                (row, value) =>
                    tstArrIncludesSomeFilterFn(
                        row,
                        CONTROL_ENTITIES_LIST_COLUMN_IDS.NAME,
                        value
                    )
            ),
            header: () => t("AccessAndAssignment_Entities_Entity"),
            size: TST_COLUMN_SIZE.L,
        }),
        columnHelper.accessor((row) => row.description ?? "", {
            id: CONTROL_ENTITIES_LIST_COLUMN_IDS.DESCRIPTION,
            cell: (info) => <span>{info.getValue()}</span>,
            enableSorting: true,
            sortingFn: "alphanumeric",
            enableColumnFilter: false,
            header: () => t("AccessAndAssignment_Entities_Description"),
            size: TST_COLUMN_SIZE.XL,
        }),
        accessRightColumn(
            CONTROL_ENTITIES_LIST_COLUMN_IDS.CONTROL_ACCESS_RIGHT,
            t("Entity_Column_Header_ControlAccessRights")
        ),
        accessRightColumn(
            CONTROL_ENTITIES_LIST_COLUMN_IDS.RISK_ACCESS_RIGHT,
            t("Entity_Column_Header_RiskAccessRights")
        ),
    ];
}

type ControlEntitiesListColumnFilters = ZodCodecMapToType<
    typeof controlEntitiesListColumnFiltersCodecMap
>;

type ControlEntitiesFilterableColumnId = keyof ControlEntitiesListColumnFilters;

function getFilterComponent(t: TFunction, column: Column<ControlEntityRow>) {
    const getFilterHelpers =
        createFilterHelpersGetter<ControlEntitiesListColumnFilters>(column);
    const columnId = column.id as ControlEntitiesFilterableColumnId;

    switch (columnId) {
        case CONTROL_ENTITIES_LIST_COLUMN_IDS.NAME: {
            const { value, setFilterValue } = getFilterHelpers(columnId);
            const values: Map<string, number> = column.getFacetedUniqueValues();
            const options = [...values.keys()].map((entity) => ({
                label: entity,
                value: entity,
            }));
            return (
                <TSTValidMultipleOptionFilterSelect
                    value={value}
                    column={column}
                    options={options}
                    placeholder={t("AccessAndAssignment_Entities_Entity")}
                    setFilterValue={setFilterValue}
                />
            );
        }
        case CONTROL_ENTITIES_LIST_COLUMN_IDS.CONTROL_ACCESS_RIGHT:
        case CONTROL_ENTITIES_LIST_COLUMN_IDS.RISK_ACCESS_RIGHT: {
            const { value, setFilterValue } = getFilterHelpers(columnId);
            const values: Map<StandardAccessRight | undefined, number> =
                column.getFacetedUniqueValues();
            const options = [...values.keys()]
                .filter((accessRight) => accessRight !== undefined)
                .map((accessRight) => ({
                    label: formatAccessRightRole(accessRight),
                    value: accessRight,
                }));
            return (
                <TSTValidMultipleOptionFilterSelect
                    value={value}
                    column={column}
                    options={options}
                    placeholder={t(
                        columnId ===
                            CONTROL_ENTITIES_LIST_COLUMN_IDS.CONTROL_ACCESS_RIGHT
                            ? "Entity_Column_Header_ControlAccessRights"
                            : "Entity_Column_Header_RiskAccessRights"
                    )}
                    setFilterValue={setFilterValue}
                />
            );
        }
        default:
            return assertNever(columnId);
    }
}
